import React, { useState, useCallback, useEffect, useRef } from "react";

import MenuModel from "./MenuModel";
import ModelViewers from "./ModelViewers";
import GroupButton from "./GroupButton";
import { scrollMenuToTop, scrollMenuToButton } from "./menuScroll";
import { menuPath } from "../../services/viewers";
import MenuUniversoClaro from "./MenuUniversoClaro";
import MenuCincoMinutos from "./MenuCincoMinutos";
import MenuOtros from "./MenuOtros";

import ViewerStyle from '../../css/Viewer.module.css';

// The menu: the viewers of the newest model the organisation has
// (services/viewers.js), then a button per group of the others.
export default function MenuLatest(props) {
    const { viewersLatest, viewersModelo1, viewersModelo0, viewersUniversoClaro, viewersCincoMinutos, othersViewers, changeViewer, location, dataViewers, menuOpenings } = props;
    const [activeMenuButton, setActiveMenuButton] = useState(Number(localStorage.getItem("selectedMenuButton")) || '');
    const [showSpecials, setShowSpecials] = useState(false);
    const [showModelSpecials, setShowModelSpecials] = useState(false);
    const [showModelOne, setShowModelOne] = useState(false);
    const [showModelZero, setShowModelZero] = useState(false);
    const [showClaroUniverse, setShowClaroUniverse] = useState(false);
    const [showFiveMinutes, setShowFiveMinutes] = useState(false);
    const [showOthersViewers, setShowOthersViewers] = useState(false);

    useEffect(() => {
        if (viewersLatest.length > 0) {
            if (activeMenuButton === '') {
                const first = viewersLatest.find(viewer => viewer.population !== 'people') || viewersLatest[0];
                setActiveMenuButton(first.id)
                localStorage.setItem("selectedMenuButton", first.id)
            }

            // The month a route belongs to: a button is drawn for the Hogares
            // viewer of its pair, so a Personas viewer marks its counterpart's.
            const slug = (location.pathname).split('/')[2];
            const current = dataViewers.find(viewer => viewer.slug === slug);
            if (current) {
                const button = current.population === 'people'
                    ? dataViewers.find(viewer => viewer.id === current.counterpart) || current
                    : current;
                setActiveMenuButton(button.id)
                localStorage.setItem("selectedMenuButton", button.id)
            }
        }
    }, [viewersLatest, dataViewers, location.pathname, activeMenuButton]);

    // The first time the viewer on screen is known, open the groups that hold
    // its button and bring it into sight: the menu started with every group
    // closed, so after a reload on a viewer inside one its button could not
    // be seen. Once per load; afterwards the menu stays where it is left.
    const placed = useRef(false);
    const [placing, setPlacing] = useState(false);
    useEffect(() => {
        if (placed.current) return;
        const slug = (location.pathname).split('/')[2];
        const current = dataViewers.find(viewer => viewer.slug === slug);
        if (!current) return;
        placed.current = true;
        const button = current.population === 'people'
            ? dataViewers.find(viewer => viewer.id === current.counterpart) || current
            : current;
        const path = menuPath(button, {
            latest: viewersLatest,
            modelo1: viewersModelo1,
            modelo0: viewersModelo0,
            universoClaro: viewersUniversoClaro,
            cincoMinutos: viewersCincoMinutos,
            others: othersViewers,
        });
        if (path) {
            setShowSpecials(path.group === 'latest' && path.specials);
            setShowModelSpecials(path.group !== 'latest' && path.specials);
            setShowModelOne(path.group === 'modelo1');
            setShowModelZero(path.group === 'modelo0');
            setShowClaroUniverse(path.group === 'universoClaro');
            setShowFiveMinutes(path.group === 'cincoMinutos');
            setShowOthersViewers(path.group === 'others');
        }
        setPlacing(true);
    }, [location.pathname, dataViewers, viewersLatest, viewersModelo1, viewersModelo0, viewersUniversoClaro, viewersCincoMinutos, othersViewers]);

    const handleButtonClick = useCallback((id, index) => {
        setActiveMenuButton(id);
        localStorage.setItem("selectedMenuButton", id)
        changeViewer(index);
    }, [changeViewer]);

    // How many viewers a group lists: one button per viewer that is not a
    // Personas one, which its counterpart's tabs reach.
    const buttons = (viewers) => viewers.filter(viewer => viewer.population !== 'people').length;

    // Opening a group, or coming back from one, starts at the top.
    useEffect(() => {
        scrollMenuToTop();
    }, [showSpecials, showModelOne, showModelZero, showClaroUniverse, showFiveMinutes, showOthersViewers]);

    // Once the menu is shown, not when the page loads: the menu is laid out
    // meanwhile, but its icons (ionicons) are drawn later and make every
    // button taller, so a button measured at load sat lower by the time the
    // menu was opened, and one at the end of a long group was out of sight
    // (Brasil Warner, at the end of Otros, on staging on 2026-10-13). Comes
    // after the scroll to the top above, which runs when a group opens.
    useEffect(() => {
        if (!placing || !menuOpenings) return;
        setPlacing(false);
        const menu = document.getElementById('menu-container');
        scrollMenuToButton(menu && menu.querySelector(`a.${ViewerStyle["active"]}`));
    }, [placing, menuOpenings]);

    const handleShowModelOne = () => {
        setShowModelOne(!showModelOne);
        setShowModelSpecials(false);
    };

    const handleShowModelZero = () => {
        setShowModelZero(!showModelZero);
        setShowModelSpecials(false);
    };

    const handleShowClaroUniverse = () => {
        setShowClaroUniverse(!showClaroUniverse);
    };

    const handleShowFiveMinutes = () => {
        setShowFiveMinutes(!showFiveMinutes);
    };

    const handleShowOthersViewers = () => {
        setShowOthersViewers(!showOthersViewers);
    };

    return (
        <div className={ViewerStyle["menu-calendar"]}>
            {(!showModelOne && !showModelZero && !showClaroUniverse && !showFiveMinutes && !showOthersViewers) && (
                <>
                    <ModelViewers
                        viewers={viewersLatest}
                        open={showSpecials}
                        onToggle={() => setShowSpecials(!showSpecials)}
                        handleButtonClick={handleButtonClick}
                        activeMenuButton={activeMenuButton}
                        dataViewers={dataViewers}
                    />
                    {!showSpecials && viewersModelo1.length > 0 && (
                        <GroupButton label="Visores Modelo 1" count={buttons(viewersModelo1)} onClick={handleShowModelOne} />
                    )}
                    {!showSpecials && viewersModelo0.length > 0 && (
                        <GroupButton label="Visores Modelo 0" count={buttons(viewersModelo0)} onClick={handleShowModelZero} />
                    )}
                    {!showSpecials && viewersUniversoClaro.length > 0 && (
                        <GroupButton label="Visores Universo Claro" count={viewersUniversoClaro.filter(viewer => viewer.population === 'households').length} onClick={handleShowClaroUniverse} />
                    )}
                    {!showSpecials && viewersCincoMinutos.length > 0 && (
                        <GroupButton label="Visores 5 Min" count={viewersCincoMinutos.length} onClick={handleShowFiveMinutes} />
                    )}
                    {!showSpecials && othersViewers.length > 0 && (
                        <GroupButton label="Otros Visores" count={buttons(othersViewers)} onClick={handleShowOthersViewers} />
                    )}
                </>
            )}

            {showModelOne && (
                <MenuModel
                    viewers={viewersModelo1}
                    setActiveMenuButton={setActiveMenuButton}
                    handleBack={handleShowModelOne}
                    showSpecials={showModelSpecials}
                    onToggleSpecials={() => setShowModelSpecials(!showModelSpecials)}
                    location={location}
                    dataViewers={dataViewers}
                    handleButtonClick={handleButtonClick}
                    activeMenuButton={activeMenuButton}
                />
            )}

            {showModelZero && (
                <MenuModel
                    viewers={viewersModelo0}
                    setActiveMenuButton={setActiveMenuButton}
                    handleBack={handleShowModelZero}
                    showSpecials={showModelSpecials}
                    onToggleSpecials={() => setShowModelSpecials(!showModelSpecials)}
                    location={location}
                    dataViewers={dataViewers}
                    handleButtonClick={handleButtonClick}
                    activeMenuButton={activeMenuButton}
                />
            )}

            {showClaroUniverse && (
                <MenuUniversoClaro
                    viewersUniversoClaro={viewersUniversoClaro}
                    setActiveMenuButton={setActiveMenuButton}
                    handleShowClaroUniverse={handleShowClaroUniverse}
                    location={location}
                    dataViewers={dataViewers}
                    handleButtonClick={handleButtonClick}
                    activeMenuButton={activeMenuButton}
                />
            )}

            {showFiveMinutes && (
                <MenuCincoMinutos
                    viewersCincoMinutos={viewersCincoMinutos}
                    handleShowFiveMinutes={handleShowFiveMinutes}
                    dataViewers={dataViewers}
                    changeViewer={changeViewer}
                    handleButtonClick={handleButtonClick}
                    activeMenuButton={activeMenuButton}
                />
            )}

            {showOthersViewers && (
                <MenuOtros
                    othersViewers={othersViewers}
                    handleShowOthersViewers={handleShowOthersViewers}
                    dataViewers={dataViewers}
                    changeViewer={changeViewer}
                    handleButtonClick={handleButtonClick}
                    activeMenuButton={activeMenuButton}
                />
            )}
        </div>
    );
}